import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form, Row, Col, InputGroup, Alert } from 'react-bootstrap';
import Select from 'react-select';
import { SuccessSwal, ErrorSwal } from '@shared/helpers/swal';
import { useConductoresSearch } from '../../hooks/useConductoresSearch';
import { useSelector } from 'react-redux';

const EditarRutaModal = ({ show, onHide, ruta, onRutaUpdated, handleUpdateRutaPrice, permisos }) => {
  const user = useSelector(state => state.auth.user);
  const [tipoPrecio, setTipoPrecio] = useState('');
  const [precioFijo, setPrecioFijo] = useState('');
  const [selectedConductor, setSelectedConductor] = useState(null);
  const [originalData, setOriginalData] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Hook para búsqueda de conductores
  const {
    conductores,
    loadingConductores,
    searchValue,
    handleSearchChange,
    clearSearch
  } = useConductoresSearch(user);

  // Permisos
  const canEdit = permisos?.canEdit || false;

  useEffect(() => {
    if (show && ruta) {
      // Inicializar valores originales
      const currentTipoPrecio = ruta.tipo_precio || 'variable';
      const currentPrecioFijo = ruta.precio_fijo || '';
      const currentConductor = ruta.usuarioSpread ? {
        value: ruta.usuario_movil_id || null,
        label: ruta.usuarioSpread,
        data: null
      } : null;

      setTipoPrecio(currentTipoPrecio);
      setPrecioFijo(currentPrecioFijo.toString());
      setSelectedConductor(currentConductor);

      // Guardar datos originales para detectar cambios
      setOriginalData({
        tipoPrecio: currentTipoPrecio,
        precioFijo: currentPrecioFijo.toString(),
        conductorId: ruta.usuario_movil_id || null,
        conductorLabel: ruta.usuarioSpread || ''
      });

      setHasChanges(false);
    }
  }, [show, ruta]);

  // Detectar cambios en los campos
  useEffect(() => {
    if (!originalData.tipoPrecio) return;

    const tipoPrecioChanged = tipoPrecio !== originalData.tipoPrecio;
    const precioFijoChanged = precioFijo !== originalData.precioFijo;
    const conductorChanged = selectedConductor?.value !== originalData.conductorId;

    setHasChanges(tipoPrecioChanged || precioFijoChanged || conductorChanged);
  }, [tipoPrecio, precioFijo, selectedConductor, originalData]);

  const handleTipoPrecioChange = (value) => {
    setTipoPrecio(value);
    if (value === 'variable') {
      setPrecioFijo('');
    }
  };

  const handlePrecioChange = (e) => {
    const value = e.target.value;
    // Solo permitir números
    if (value === '' || /^\d+$/.test(value)) {
      setPrecioFijo(value);
    }
  };

  const handleConductorChange = (selectedOption) => {
    setSelectedConductor(selectedOption);
  };

  const isFormValid = () => {
    if (tipoPrecio === 'fijo') {
      const precio = parseInt(precioFijo);
      return precioFijo.trim() !== '' && !isNaN(precio) && precio > 0;
    }
    return true;
  };

  const getChangeSummary = () => {
    const changes = [];
    
    if (tipoPrecio !== originalData.tipoPrecio) {
      changes.push(`Tipo de precio: ${originalData.tipoPrecio} → ${tipoPrecio}`);
    }
    
    if (precioFijo !== originalData.precioFijo && tipoPrecio === 'fijo') {
      changes.push(`Precio fijo: ${originalData.precioFijo || '0'} → ${precioFijo}`);
    }
    
    if (selectedConductor?.value !== originalData.conductorId) {
      changes.push(`Usuario Spread: ${originalData.conductorLabel || 'Sin asignar'} → ${selectedConductor?.label || 'Sin asignar'}`);
    }
    
    return changes;
  };

  const handleConfirm = () => {
    if (!hasChanges || !isFormValid()) return;
    
    // Si hay cambio de conductor, mostrar confirmación
    if (selectedConductor?.value !== originalData.conductorId) {
      setShowConfirmModal(true);
    } else {
      // Solo cambio de precio, confirmar directamente
      handleSave();
    }
  };

  const handleSave = async () => {
    if (!hasChanges || !isFormValid()) return;
    
    setLoading(true);
    setShowConfirmModal(false);
    
    try {
      const updateData = {};
      
      // Solo incluir campos que cambiaron
      if (tipoPrecio !== originalData.tipoPrecio) {
        updateData.tipo_precio = tipoPrecio;
      }
      
      if (tipoPrecio === 'fijo' && precioFijo !== originalData.precioFijo) {
        updateData.precio_fijo = parseInt(precioFijo);
      }
      
      if (selectedConductor?.value !== originalData.conductorId) {
        updateData.usuario_movil_id = selectedConductor?.value || null;
      }
      
      const response = await handleUpdateRutaPrice(ruta.id, updateData);
      
      if (response && response.success) {
        SuccessSwal('Ruta actualizada correctamente');
        // Notificar al componente padre con la ruta actualizada
        if (onRutaUpdated && response.data) {
          onRutaUpdated(response.data);
        }
        handleClose();
      } else {
        ErrorSwal('Error al actualizar la ruta');
      }
    } catch (error) {
      console.error('Error al actualizar ruta:', error);
      ErrorSwal('Error al actualizar la ruta');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTipoPrecio('');
    setPrecioFijo('');
    setSelectedConductor(null);
    setOriginalData({});
    setHasChanges(false);
    setShowConfirmModal(false);
    clearSearch();
    onHide();
  };

  const formatMonto = (monto) => {
    if (!monto && monto !== 0) return '';
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(monto);
  };

  const getCurrentPriceInfo = () => {
    if (!ruta) return '';
    
    if (ruta.tipo_precio === 'fijo') {
      return `Actualmente: Precio Fijo ${formatMonto(ruta.precio_fijo)}`;
    } else {
      return `Actualmente: Precio P*Q ${formatMonto(ruta.estadisticas?.precio_efectivo || 0)}`;
    }
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Editar Ruta</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {ruta && (
            <div>
              {/* Información básica de la ruta */}
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Ruta:</strong> {ruta.ruta}
                </Col>
                <Col md={6}>
                  <strong>Fecha:</strong> {ruta.fecha}
                </Col>
              </Row>

              <hr />

              {/* Edición de Usuario Spread */}
              {canEdit && (
                <div className="mb-4">
                  <h5>Usuario Spread</h5>
                  <Row>
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>
                          Conductor Asignado
                          <small className="text-muted ms-2">
                            (Actual: {ruta.usuarioSpread || 'Sin asignar'})
                          </small>
                        </Form.Label>
                        <Select
                          value={selectedConductor}
                          onChange={handleConductorChange}
                          options={conductores}
                          placeholder="Escriba para buscar conductor..."
                          isClearable
                          isLoading={loadingConductores}
                          isSearchable
                          noOptionsMessage={() => 
                            searchValue.length < 2 
                              ? 'Escriba al menos 2 caracteres para buscar'
                              : 'No se encontraron conductores'
                          }
                          onInputChange={handleSearchChange}
                          inputValue={searchValue}
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              minHeight: '38px'
                            })
                          }}
                        />
                        <Form.Text className="text-muted">
                          Busque por nombre, apellido o usuario spread del conductor
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              )}

              <hr />

              {/* Edición de Tipo de Precio */}
              <div className="mb-4">
                <h5>Tipo de Precio</h5>
                <Form.Text className="text-muted d-block mb-2">
                  {getCurrentPriceInfo()}
                </Form.Text>
                
                <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Tipo de Precio</Form.Label>
                      <Form.Select
                        value={tipoPrecio}
                        onChange={(e) => handleTipoPrecioChange(e.target.value)}
                      >
                        <option value="variable">Precio P*Q (Variable)</option>
                        <option value="fijo">Precio Fijo</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  {tipoPrecio === 'fijo' && (
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Monto Precio Fijo</Form.Label>
                        <InputGroup>
                          <InputGroup.Text>$</InputGroup.Text>
                          <Form.Control
                            type="text"
                            value={precioFijo}
                            onChange={handlePrecioChange}
                            placeholder="Ingrese el monto"
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                  )}
                </Row>
              </div>

              {/* Resumen de cambios */}
              {hasChanges && (
                <Alert variant="info">
                  <strong>Cambios detectados:</strong>
                  <ul className="mb-0 mt-2">
                    {getChangeSummary().map((change, index) => (
                      <li key={index}>{change}</li>
                    ))}
                  </ul>
                </Alert>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleClose}>
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            onClick={handleConfirm}
            disabled={!hasChanges || !isFormValid() || loading}
          >
            {loading ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Confirmación */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Cambio de Usuario</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <strong>¿Está seguro de realizar este cambio?</strong>
          </Alert>
          <p>
            Se cambiará el usuario asignado de:
          </p>
          <div className="bg-light p-3 rounded">
            <strong>De:</strong> {originalData.conductorLabel || 'Sin asignar'}<br />
            <strong>A:</strong> {selectedConductor?.label || 'Sin asignar'}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowConfirmModal(false)}>
            Cancelar
          </Button>
          <Button variant="warning" onClick={handleSave}>
            Confirmar Cambio
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

EditarRutaModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  ruta: PropTypes.object,
  onRutaUpdated: PropTypes.func,
  handleUpdateRutaPrice: PropTypes.func.isRequired,
  permisos: PropTypes.object
};

export default EditarRutaModal;
